import { Component, OnInit, OnDestroy } from '@angular/core';
import {
  Gender,
  Participant,
  Student,
} from 'src/app/shared/modals/participant';
import { AuthService } from 'src/app/shared/services/auth.service';
import { StoreService } from 'src/app/shared/services/store.service';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Camp } from 'src/app/shared/modals/camp';
import * as moment from 'moment';
import { WebServiceService } from 'src/app/shared/services/web/web-service.service';
import { ErrorFn, User } from 'firebase/auth';

@Component({
  selector: 'app-sign-up',
  templateUrl: './sign-up.component.html',
  styleUrls: ['./sign-up.component.scss'],
})
export class SignUpComponent implements OnInit, OnDestroy {
  private sub: any;
  camp: Camp | undefined;
  submitStatus: 'loading' | 'questionUser' | 'normal' | 'success' | 'error' | 'error-email-exists' | 'disabled' = 'loading';
  error: any;
  user: any = undefined;
  private authSub: any;

  minDate: Date;
  maxDate: Date;
  genders = [Gender.male, Gender.female];
  students = ['', Student.student, Student.universityStudent, Student.trainee];
  hidePassword = true;
  form = new FormGroup({
    firstName: new FormControl('', [Validators.required]),
    lastName: new FormControl('', [Validators.required]),
    birthdate: new FormControl('', [Validators.required]),
    gender: new FormControl('', [Validators.required]),
    email: new FormControl('', [Validators.required, Validators.email]),
    phoneNumber: new FormControl('', [
      Validators.required,
      Validators.pattern('^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$'),
    ]),
    parentEmail: new FormControl(),
    street: new FormControl('', [Validators.required]),
    city: new FormControl('', [Validators.required]),
    postalCode: new FormControl('', [
      Validators.required,
      Validators.pattern('^[0-9]{5}$'),
    ]),
    student: new FormControl(''),
    comment: new FormControl(''),
    password: new FormControl('', [
      Validators.required,
      Validators.minLength(6),
    ]),
    dataProtection: new FormControl(false, [
      Validators.required,
      Validators.requiredTrue,
    ]),
  });

  constructor(
    public authService: AuthService,
    public storeService: StoreService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private webService: WebServiceService,
  ) {
    const currentYear = new Date().getFullYear();
    this.minDate = new Date(currentYear - 100, 0, 1);
    this.maxDate = new Date();
  }

  async ngOnInit(): Promise<void> {
    this.authSub = this.authService.afAuth.authState.subscribe(async user => {
      if (!user) {
        return;
      }
      this.user = user;
      this.navigateIfLoggedInAndCamp();
    });

    this.sub = this.activatedRoute.params.subscribe((params) => {
      this.storeService
        .getCampById(params['campId'])
        .then((camp: Camp | undefined) => {
          if (!camp) {
            this.router.navigate(['register-user/wrong-link']);
            return;
          }
          this.camp = camp;
          this.navigateIfLoggedInAndCamp();
          this.submitStatus = camp.disabled ? 'disabled' : 'questionUser';
        })
        .catch(() => {
          this.router.navigate(['register-user/wrong-link']);
        });
    });
  }

  public async signUp(): Promise<void> {
    if (!this.form.valid || !this.camp) {
      window.alert('please fill up all the required fields');
      return;
    }

    if (this.getFormValueString('email') == this.getFormValueString('parentEmail')) {
      window.alert('The Participant Email and Parent Email must not be the same!');
      return;
    }

    this.form.disable();
    this.authSub?.unsubscribe();
    const user = await this.authService.SignUp(
      this.getFormValueString('email'),
      this.getFormValueString('password')
    ).catch((error: any) => {
      if (error.code == 'auth/email-already-in-use') {
        this.submitStatus = 'error-email-exists';
        this.error = 'error-email-exists';
        return;
      }
      this.error = error;
      this.submitStatus = 'error';
      return;
    }) as User;

    if (!user?.uid && !this.submitStatus.startsWith('error')) {
      if (!this.error)
        this.error = 'custom: no user.uid';
      this.submitStatus = 'error';
      this.form.enable();
      return;
    }

    if(this.submitStatus.startsWith('error')) {
      return;
    }

    const participant: Participant = {
      id: '',
      email: this.getFormValueString('email'),
      firstName: this.getFormValueString('firstName'),
      lastName: this.getFormValueString('lastName'),
      birthdate: this.getFormValue('birthdate') as Date,
      gender: this.getFormValue('gender') as Gender,
      street: this.getFormValueString('street'),
      city: this.getFormValueString('city'),
      postalcode: this.getFormValueString('postalCode'),
      student: this.getFormValue('student') as Student,
      parentemail: this.getFormValueString('parentEmail'),
      comment: this.getFormValueString('comment'),
      dataProtection: this.getFormValue('dataProtection') as boolean,
      phoneNumber: '' + this.getFormValueString('phoneNumber'),
      photoConsent: {},
      registrationDate: new Date(),
      campRegestrationIDs: [],
    };

    try {
      this.webService.postRegisterNewUserToCamp(participant, user.uid, this.camp.campId, await user.getIdToken()).subscribe((response: any) => {
        if (response.status == 200) {
          this.submitStatus = 'success';
          // Refresh groups now that the user document has been created by the backend
          this.webService.getUserGroups().subscribe({
            next: (groups: any) => {
              localStorage.setItem('groups', JSON.stringify(groups.data));
              this.authService.groups$.next(groups.data);
            }
          });
          return;
        } else {
          this.submitStatus = 'error';
          this.error = response.message;
        }
        throw new Error('test');
      });
    } catch {
      this.error = 'Something went wrong';
      this.submitStatus = 'error';
      this.form.enable();
    }
  }

  private getFormValueString(fieldName: string): string {
    return this.form.get(fieldName)?.value as string;
  }
  private getFormValue(fieldName: string): unknown {
    return this.form.get(fieldName)?.value;
  }

  public shouldShowParentEmail(): boolean {
    const result = this.getParticipantAge() < 18;
    if (result) {
      this.form.controls['parentEmail'].setValidators([
        Validators.required,
        Validators.email,
      ]);
    } else {
      this.form.controls['parentEmail'].setValidators([]);
    }
    this.form.controls['parentEmail'].updateValueAndValidity();
    return result;
  }

  public getParticipantAge(): number {
    const birthdate = new Date(this.form.get('birthdate')?.value as string);
    const eventStartDate = moment(this.camp?.startDate);

    if (!birthdate || !eventStartDate) {
      return 0;
    }
    return eventStartDate.diff(birthdate, 'years');
  }
  public getMinAge(): number {
    return this.camp?.minAge ? this.camp.minAge : 0;
  }

  getErrorEmailContent(): string {
    return '?subject=Something went wrong with the regestration&body=Form: "' + JSON.stringify(this.form.getRawValue()) + '"\nError: "' + JSON.stringify(this.error) + '"';
  }

  participantWithExistingUser() {
    this.router.navigate(['sign-in'], {
      queryParams: {
        redirectURL: "/register-existing-user/" + this.camp?.campId,
      }
    });
  }

  ngOnDestroy() {
    this.sub?.unsubscribe();
    this.authSub?.unsubscribe();
  }

  navigateIfLoggedInAndCamp() {
    if (!this.user || !this.camp) {
      return;
    }
    this.router.navigate(['register-existing-user', this.camp?.campId]);
  }
}
